import {
  ArrowRight,
  ChartLineUp,
  Info,
  TrendDown,
  TrendUp,
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';

import { CardErrorState, ERROR_BOX_CLASS } from '../common/CardErrorState';
import { paths } from '../../routes/paths';
import { formatEUR } from '../../utils/money';
import pt from '../../locales/pt';
import { cn } from '../../utils/cn';

import { buildSparklineBars } from '../../lib/selfieDisplayUtils';

const MONTHS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

/**
 * `.pen` → `ds/card/networth-hero` (`uOlVI`) e `--empty` (`lWZe9`).
 *
 * Cartão de superfície (`$surface-card` / `$border` / `$radius-ds-lg`,
 * `padding $space-5`, sem sombra e sem gap entre blocos — o espaçamento vem do
 * `padding-top $space-5` do bloco do gráfico).
 *
 * Estado com dados:
 * - `head` (`gap $space-2`): `head-row` (eyebrow `$type-section` + info 14,
 *   `space_between`, `pb $space-1`) · readout `$type-display-kpi` ·
 *   `delta` (ícone 16 + valor + % + «face a {mês}»), todos `$type-body-sm`
 * - `chart` (`gap $space-2`, `pt $space-5`): 12 barras `$sparkline-bar`
 *   (`gap 3`, raio 3) com a última em `$sage-500`, numa banda de 56px
 *   (96px na instância de 1280) · escala «desde {mês}» / «{delta} no período»
 *
 * Estado vazio (`--empty`): readout «—» em `$fg-muted`, título dedicado
 * `$type-title-card`, descrição, banda vazia de 56px com contorno `$border`,
 * chip `$sage-tint` 32 com `chart-line-up`, e o link «Fazer a primeira selfie».
 *
 * Ambos ganharam bloco `error` (chip `warning-circle` + retry).
 */
const CARD_CLASS =
  'flex w-full min-w-0 flex-col rounded-ds-lg border border-border bg-card p-space-5';

/** `.pen`: barras 3px de gap e 3px de raio — literais do master, sem token. */
const BAR_CLASS = 'min-w-0 flex-1 rounded-[3px]';
const BARS_ROW_CLASS = 'flex h-14 items-end gap-[3px] lg:h-24';

function NetWorthEyebrow() {
  return (
    <div className="flex min-w-0 items-center justify-between gap-space-3 pb-space-1">
      <p className="type-section text-muted-foreground">{pt.dashboard.netWorthHero.label}</p>
      <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
    </div>
  );
}

/**
 * Z2 — património dominante (user estabelecido).
 * @param {{
 *   timeline?: object[],
 *   isError?: boolean,
 *   onRetry?: () => void,
 *   className?: string,
 * }} props
 */
export function DashboardNetWorthHero({ timeline, isError = false, onRetry, className }) {
  const hasData = timeline?.length > 0;

  if (isError) {
    /** `.pen` → `error`: bloco centrado dentro do mesmo cartão (altura 186). */
    return (
      <div className={cn(CARD_CLASS, ERROR_BOX_CLASS, className)}>
        <CardErrorState message={pt.common.netWorthLoadError} onRetry={onRetry} />
      </div>
    );
  }

  if (!hasData) {
    return (
      <div className={cn(CARD_CLASS, className)}>
        <div className="flex min-w-0 flex-col gap-space-2">
          <NetWorthEyebrow />
          <p className="type-display-kpi text-muted-foreground">—</p>
          <p className="type-title-card text-foreground">
            {pt.dashboard.netWorthHero.emptyTitle}
          </p>
          <p className="type-body-sm text-muted-foreground">
            {pt.dashboard.netWorthHero.emptyDescription}
          </p>
        </div>

        <div className="pt-space-5">
          <div className="flex h-14 items-center justify-center gap-space-2 rounded-ds-md border border-border">
            <span
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-ds-full bg-sage-tint"
              aria-hidden
            >
              <ChartLineUp className="h-4 w-4 text-sage-on-tint" />
            </span>
            <span className="type-label-meta text-muted-foreground">
              {pt.dashboard.netWorthHero.emptyChartLabel}
            </span>
          </div>
        </div>

        <div className="flex pt-space-4">
          <Link
            to={paths.selfieNew}
            className="type-body-sm inline-flex items-center gap-space-1 font-semibold text-sage-on-tint hover:underline"
          >
            {pt.dashboard.netWorthHero.emptyLinkCta}
            <ArrowRight className="h-3.5 w-3.5" aria-hidden />
          </Link>
        </div>
      </div>
    );
  }

  const data = timeline.map((row) => ({
    label: `${MONTHS[row.reference_month - 1]}/${String(row.reference_year).slice(-2)}`,
    value: Number(row.net_worth_eur || 0),
  }));
  const latest = data[data.length - 1];
  const previous = data.length > 1 ? data[data.length - 2] : null;
  const change = previous ? latest.value - previous.value : null;
  const changePct =
    previous && previous.value !== 0
      ? ((change / Math.abs(previous.value)) * 100).toFixed(1)
      : null;
  const { bars } = buildSparklineBars(timeline);

  const previousMonthLabel =
    previous && timeline.length > 1
      ? MONTHS[timeline[timeline.length - 2].reference_month - 1]?.toLowerCase()
      : null;

  const windowChange = data.length > 1 ? latest.value - data[0].value : null;

  const isDecline = change !== null && change < 0;
  const ChangeIcon = isDecline ? TrendDown : TrendUp;
  const isWindowDecline = windowChange !== null && windowChange < 0;
  const deltaTone = isDecline ? 'text-warning-fg' : 'text-success-fg';

  return (
    <div className={cn(CARD_CLASS, className)}>
      {/* `.pen` › `head` */}
      <div className="flex min-w-0 flex-col gap-space-2">
        <NetWorthEyebrow />
        <p className="type-display-kpi text-foreground">{formatEUR(latest.value)}</p>

        {change !== null ? (
          <div className="flex min-w-0 flex-wrap items-center gap-space-2">
            <ChangeIcon className={cn('h-4 w-4 shrink-0', deltaTone)} aria-hidden />
            <span className={cn('type-body-sm font-display font-semibold tnum', deltaTone)}>
              {change >= 0 ? '+' : ''}
              {formatEUR(change)}
            </span>
            {changePct != null ? (
              <span className={cn('type-body-sm', deltaTone)}>
                ({change >= 0 ? '+' : ''}
                {changePct}%)
              </span>
            ) : null}
            {previousMonthLabel ? (
              <span className="type-body-sm text-muted-foreground">
                {pt.dashboard.netWorthHero.vsMonth.replace('{month}', previousMonthLabel)}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* `.pen` › `chart` */}
      <div className="flex min-w-0 flex-col gap-space-2 pt-space-5">
        <div className={BARS_ROW_CLASS} aria-hidden>
          {bars.map((bar, index) => (
            <span
              key={index}
              className={cn(BAR_CLASS, bar.isLatest ? 'bg-sage-500' : 'bg-sparkline-bar')}
              style={{ height: `${bar.heightPct}%` }}
            />
          ))}
        </div>
        <div className="flex items-center justify-between gap-space-2 text-muted-foreground">
          <span className="type-label-meta">
            {pt.dashboard.netWorthHero.chartFrom.replace('{month}', data[0].label)}
          </span>
          {windowChange != null ? (
            <span className={cn('type-label-meta text-right', isWindowDecline && 'text-warning-fg')}>
              {pt.dashboard.netWorthHero.chartWindowChange.replace(
                '{amount}',
                `${windowChange >= 0 ? '+' : ''}${formatEUR(windowChange)}`,
              )}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default DashboardNetWorthHero;
